import { type FC, useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Box, Chip, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { VideoFileQuery } from "@/shared/graphql";
import type { VideoFileStatus } from "@/shared/components";
import type { DeviceMode } from "../../model/types";
import { byDevice } from "./byDevice";

export interface BackgroundVideoProps {
  desktopVideoId?: string | null;
  mobileVideoId?: string | null;
  device: DeviceMode;
  brightness?: number | null;
}

/** Пока идёт транскодинг, статус спрашиваем раз в 5 секунд. */
const VIDEO_POLL_INTERVAL_MS = 5000;

const FINAL_VIDEO_STATUSES: VideoFileStatus[] = ["READY", "FAILED"];

const Root = styled(Box)({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: -1,
});

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "brightness",
})<{ brightness?: number | null }>(({ theme, brightness }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  filter:
    brightness === null || brightness === undefined
      ? undefined
      : theme.palette.mode === "dark"
        ? `brightness(${brightness})`
        : `opacity(${brightness})`,

  "& video, & img": {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

/**
 * Видеофон обложки. zIndex как у картинки (-1), но в DOM видео идёт после
 * неё, поэтому лежит поверх: пока файл не READY, партнёр видит свою картинку
 * и чип статуса, как слоит витрина.
 *
 * HLS через hls.js там, где нет нативной поддержки (Safari играет m3u8 сам).
 * Мобильный режим без своего видео играет десктопное, как на витрине.
 */
export const BackgroundVideo: FC<BackgroundVideoProps> = ({
  desktopVideoId,
  mobileVideoId,
  device,
  brightness,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [visible, setVisible] = useState(false);

  const videoId = byDevice(device, {
    mobile: mobileVideoId || desktopVideoId,
    desktop: desktopVideoId,
  });

  const { data, startPolling, stopPolling } = useQuery(VideoFileQuery, {
    variables: { id: videoId },
    skip: !videoId,
    fetchPolicy: "cache-and-network",
  });

  const videoFile = data?.videoFile;
  const status: VideoFileStatus | undefined = videoFile?.status;
  const playlistUrl = status === "READY" ? videoFile?.playlistUrl : null;
  const posterUrl = videoFile?.posterUrl;

  useEffect(() => {
    if (!videoId) {
      return undefined;
    }

    if (status && FINAL_VIDEO_STATUSES.includes(status)) {
      stopPolling();
      return undefined;
    }

    startPolling(VIDEO_POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [videoId, status, startPolling, stopPolling]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !playlistUrl) {
      return undefined;
    }

    setVisible(false);

    let hls: { destroy(): void } | null = null;
    let native = false;
    let cancelled = false;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = playlistUrl;
      native = true;
    } else {
      // Динамический импорт: hls.js не нужен ни обложкам без видео, ни
      // Safari с нативным HLS.
      const attachHls = async () => {
        const { default: Hls } = await import("hls.js");

        if (cancelled || !Hls.isSupported()) {
          return;
        }

        // Фоновой петле большой буфер не нужен: превью не должно тянуть
        // трафик ради кадров, которые никто не увидит.
        const instance = new Hls({
          maxBufferLength: 10,
          maxMaxBufferLength: 15,
          backBufferLength: 10,
          capLevelToPlayerSize: true,
        });
        // Восстановление после фатальных ошибок: без него моргнувшая сеть
        // навсегда замораживает превью до перемонтирования.
        instance.on(Hls.Events.ERROR, (_event, errorData) => {
          if (!errorData.fatal) {
            return;
          }

          if (errorData.type === Hls.ErrorTypes.NETWORK_ERROR) {
            instance.startLoad();
          } else if (errorData.type === Hls.ErrorTypes.MEDIA_ERROR) {
            instance.recoverMediaError();
          } else {
            instance.destroy();
          }
        });
        instance.loadSource(playlistUrl);
        instance.attachMedia(video);
        hls = instance;
      };

      void attachHls();
    }

    return () => {
      cancelled = true;
      hls?.destroy();

      if (native) {
        // Нативный путь (Safari): без сброса источника элемент продолжает
        // буферизовать поток после смены видео или закрытия редактора.
        video.removeAttribute("src");
        video.load();
      }
    };
  }, [playlistUrl]);

  if (!videoId || !videoFile) {
    return null;
  }

  return (
    <>
      <Root
        data-testid="CoverBackgroundVideo"
        sx={{
          opacity: playlistUrl && !visible ? 0 : 1,
          transition: "opacity 1s ease",
        }}
      >
        <Container brightness={brightness}>
          {playlistUrl ? (
            <video
              ref={videoRef}
              poster={posterUrl || undefined}
              onCanPlay={() => setVisible(true)}
              controls={false}
              muted
              autoPlay
              playsInline
              loop
            />
          ) : (
            posterUrl && (
              <img
                src={posterUrl}
                alt={t("platformCustomCovers.preview.mediaAlt")}
              />
            )
          )}
        </Container>
      </Root>

      {status && status !== "READY" && (
        <Chip
          data-testid="CoverVideoStatus"
          size="small"
          color={status === "FAILED" ? "error" : "default"}
          label={t(`videoFiles.status.${status}`)}
          // Чип соседствует с Root, а не лежит внутри него: Root уехал бы под
          // затемнение обложки вместе со своим zIndex -1, да и яркость фона
          // чипа касаться не должна. Позиционируется от корня CoverView
          // (position: relative). Клик по превью по-прежнему открывает
          // боковую панель, поэтому события мыши выключены.
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      )}
    </>
  );
};
